import React, { memo, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, EmptyState, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { WALLETS_NUMBER_OF_ROWS } from 'constants/General'
import { useWalletControllerContext } from 'hooks/useWalletControllerContext'
import { WalletSortOptions } from 'pages/Wallets/types'
import { WalletType } from 'types/types'
import { TWalletType, WalletTypes } from 'types/wallets'
import { WalletSheet } from '../Sheet/WalletSheet'
import { WalletFilter } from './WalletFilter'
import WalletList from './WalletList'

interface WalletTableProps {
  wallets: WalletType[]
  walletType: TWalletType
  searchQuery?: string
  clearFilters?: () => void
  onSearchTermChange?: React.Dispatch<SetStateAction<string>>
  isWalletBeingSearched?: boolean
  debouncedSearch?: string
  sortOption: WalletSortOptions
  onSortOptionChanged: React.Dispatch<SetStateAction<WalletSortOptions>>
}

const WalletTable = ({
  searchQuery,
  onSearchTermChange,
  wallets,
  walletType,
  clearFilters,
  isWalletBeingSearched,
  debouncedSearch,
  sortOption,
  onSortOptionChanged,
}: WalletTableProps) => {
  const { t } = useTranslation()
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const { isLoading, isSuccess, hasErrors } = useWalletControllerContext()

  if (hasErrors) {
    return (
      <EmptyState
        testId="wallets-loading-error"
        LottiePlayer={<Lottie src="connection-error" />}
        title={t('requestErrors.pageErrorIntro')}
        description={
          <span>
            {`${t('requestErrors.comeBackLater')} `}
            <Button variant="ghost" noPadding onClick={() => globalThis.location.reload()}>
              {t('requestErrors.refreshPage')}
            </Button>
          </span>
        }
      />
    )
  }

  return (
    <>
      <Box paddingB={24} flex width={'full'}>
        <WalletFilter
          wallets={wallets}
          searchQuery={searchQuery}
          onSearchTermChange={onSearchTermChange}
          sortOption={sortOption}
          onSortOptionChanged={onSortOptionChanged}
        />
      </Box>

      {!wallets?.length && isWalletBeingSearched && !isLoading && (
        <NoSearchResults clearSearchCriteria={clearFilters} />
      )}

      {isSuccess && wallets?.length === 0 && !isLoading && !debouncedSearch && (
        <EmptyState
          testId="no-wallets-content"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('wallets.emptyStateForWalletsHeadline')}
          description={
            walletType === WalletTypes.FIAT ? (
              <span>
                {t('createFiatWalletDisclaimer')}
                <ExternalLink href="https://help.bvnk.com/hc/en-us/articles/13995679766930">
                  {` ${t('learnMore')}`}
                </ExternalLink>
              </span>
            ) : (
              t('wallets.emptyStateForWalletsDescription')
            )
          }
        />
      )}

      {isLoading && <RowsSkeletonLoader rows={WALLETS_NUMBER_OF_ROWS} />}

      {!isLoading && wallets?.length > 0 && (
        <WalletList setIsSheetOpen={setIsSheetOpen} wallets={wallets} walletType={walletType} />
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
    </>
  )
}

export default memo(WalletTable)
